import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { useAuth, isGlobalAdmin } from '../hooks/useAuth';
import { toast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmDialog';

type ProjectType = 'pi-based' | 'regular-sprints' | 'weekly' | 'monthly';

interface Project {
  projectId: string;
  shortName: string;
  description: string;
  projectType?: ProjectType;
  members: { userId: string; role: string; accessLevel: string }[];
  createdAt: string;
  isDeleted?: boolean;
  deletedAt?: string;
}

const PROJECT_TYPE_OPTIONS: { value: ProjectType; label: string; description: string }[] = [
  { value: 'pi-based', label: 'PI-based (SAFe)', description: '7 PIs, 26 two-week sprints' },
  { value: 'regular-sprints', label: 'Regular Sprints', description: 'Configurable two-week sprints without PIs' },
  { value: 'weekly', label: 'Weekly', description: '52 one-week periods' },
  { value: 'monthly', label: 'Monthly', description: '12 monthly periods' },
];

interface ProjectsResponse {
  items: Project[];
  archivedItems?: Project[];
}

export default function Projects() {
  const api = useApi();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const [showCreate, setShowCreate] = useState(false);
  const [newProject, setNewProject] = useState({ shortName: '', description: '', projectType: 'pi-based' as ProjectType });
  const [showArchived, setShowArchived] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.get<ProjectsResponse>('/api/projects'),
  });

  const createMutation = useMutation({
    mutationFn: (data: { shortName: string; description: string; projectType: ProjectType }) =>
      api.post('/api/projects', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      setShowCreate(false);
      setNewProject({ shortName: '', description: '', projectType: 'pi-based' });
      toast.success('Project created successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to create project');
    },
  });

  const restoreMutation = useMutation({
    mutationFn: (projectId: string) =>
      api.post(`/api/projects/${projectId}/restore`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast.success('Project restored successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to restore project');
    },
  });

  const handleRestore = async (project: Project) => {
    const confirmed = await confirm({
      title: 'Restore Project',
      message: `Restore "${project.shortName}"? Note: Archived teams within this project must be restored separately.`,
      confirmText: 'Restore',
      variant: 'info',
    });

    if (confirmed) {
      restoreMutation.mutate(project.projectId);
    }
  };

  const projects = data?.items || [];
  const archivedProjects = data?.archivedItems || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Projects</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Manage your projects and their teams</p>
        </div>
        {isGlobalAdmin(user) && (
          <button onClick={() => setShowCreate(true)} className="btn-primary">
            New Project
          </button>
        )}
      </div>

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50">
          <div className="card max-w-md w-full mx-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Create New Project</h2>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                createMutation.mutate(newProject);
              }}
            >
              <div className="space-y-4">
                <div>
                  <label className="label">Short Name</label>
                  <input
                    type="text"
                    className="input"
                    value={newProject.shortName}
                    onChange={(e) => setNewProject({ ...newProject, shortName: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="label">Description</label>
                  <textarea
                    className="input"
                    rows={3}
                    value={newProject.description}
                    onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                  />
                </div>
                <div>
                  <label className="label">Project Type</label>
                  <div className="space-y-2">
                    {PROJECT_TYPE_OPTIONS.map((option) => (
                      <label key={option.value} className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 dark:border-dark-500 cursor-pointer hover:bg-gray-50 dark:hover:bg-dark-600">
                        <input
                          type="radio"
                          name="projectType"
                          value={option.value}
                          checked={newProject.projectType === option.value}
                          onChange={(e) => setNewProject({ ...newProject, projectType: e.target.value as ProjectType })}
                          className="mt-0.5 h-4 w-4 text-primary-500 border-gray-300 dark:border-dark-400 focus:ring-primary-500"
                        />
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white text-sm">{option.label}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{option.description}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button type="button" onClick={() => setShowCreate(false)} className="btn-outline">
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={createMutation.isPending}>
                  {createMutation.isPending ? 'Creating...' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Projects grid */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent"></div>
        </div>
      ) : projects.length === 0 ? (
        <div className="card text-center py-12">
          <p className="text-gray-600 dark:text-gray-400">No projects yet</p>
          {isGlobalAdmin(user) && (
            <button onClick={() => setShowCreate(true)} className="btn-primary mt-4">
              Create your first project
            </button>
          )}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project) => (
            <Link
              key={project.projectId}
              to={`/projects/${project.projectId}`}
              className="card hover:shadow-md transition-shadow"
            >
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{project.shortName}</h3>
                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-200 dark:bg-dark-500 text-gray-600 dark:text-gray-300">
                  {PROJECT_TYPE_OPTIONS.find((o) => o.value === project.projectType)?.label || 'PI-based'}
                </span>
              </div>
              <p className="text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">{project.description || 'No description'}</p>
              <div className="flex items-center gap-4 mt-4 text-sm text-gray-500 dark:text-gray-400">
                <span>{project.members?.length || 0} members</span>
                <span>Created {new Date(project.createdAt).toLocaleDateString()}</span>
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Archived Projects Section (Super Admin Only) */}
      {isGlobalAdmin(user) && archivedProjects.length > 0 && (
        <div className="mt-12">
          <button
            onClick={() => setShowArchived(!showArchived)}
            className="flex items-center gap-2 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
          >
            <svg
              className={`w-5 h-5 transition-transform ${showArchived ? 'rotate-90' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <h2 className="text-lg font-semibold">Archived Projects ({archivedProjects.length})</h2>
          </button>

          {showArchived && (
            <div className="mt-4 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {archivedProjects.map((project) => (
                <div
                  key={project.projectId}
                  className="card bg-gray-100 dark:bg-dark-600 border-gray-300 dark:border-dark-500 opacity-75"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-500 dark:text-gray-400">{project.shortName}</h3>
                      <p className="text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">{project.description || 'No description'}</p>
                    </div>
                    <span className="px-2 py-1 text-xs font-medium bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-full">
                      Archived
                    </span>
                  </div>
                  <div className="flex items-center justify-between mt-4">
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      Archived {project.deletedAt ? new Date(project.deletedAt).toLocaleDateString() : ''}
                    </span>
                    <button
                      onClick={() => handleRestore(project)}
                      disabled={restoreMutation.isPending}
                      className="text-sm font-medium text-primary-500 hover:text-primary-600"
                    >
                      Restore
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
